import { useEffect, useRef, useState } from "react";

import Button from "../../../ui/buttons/Button";
import AttachmentPickerModal from "../../templates/AttachmentPickerModal";
import { templatesService } from "../../../services/templates.service";
import { extractApiErrorMessage } from "../../../utils/apiError";
import {
  formatFileSize,
  getAttachmentDisplayName,
  getAttachmentSizeInBytes,
} from "../../../utils/fileUtils";
import {
  ACCEPT_ATTRIBUTE,
  mediaTypeOf,
  validateWhatsAppFile,
} from "../../../utils/whatsappMedia";

import styles from "./LeadWhatsApp.module.css";

export const WINDOW_CLOSED_NOTICE =
  "Pasaron más de 24 horas desde el último mensaje del contacto, así que WhatsApp no deja escribirle texto libre. Para retomar la conversación, mandale una plantilla aprobada.";
export const NO_INBOUND_NOTICE =
  "El contacto todavía no te escribió. Para mandarle el primer mensaje, WhatsApp pide una plantilla aprobada.";
export const PHONE_APP_HINT =
  "Desde la app WhatsApp Business del teléfono también podés escribirle, y el mensaje aparece acá.";

export const UPLOAD_ERROR_MESSAGE = "No se pudo subir el archivo.";
export const NO_ACCOUNT_MESSAGE =
  "No se puede adjuntar: la conversación no tiene una cuenta asociada.";
export const WITHOUT_SAVING_NOTE = "sin guardar en la cuenta";
export const DROP_HINT = "Soltá el archivo para adjuntarlo";
const SENT_WITHOUT_SAVING_TYPES = new Set(["video", "audio"]);
const EXTENSION_PATTERN = /\.[^./]+$/;

const attachmentFileName = (attachment) => {
  const displayName = getAttachmentDisplayName(attachment);
  if (EXTENSION_PATTERN.test(displayName)) return displayName;
  const storedPath = typeof attachment?.file === "string" ? attachment.file.split("?")[0] : "";
  const storedName = decodeURIComponent(storedPath.split("/").pop() || "");
  return storedName || displayName;
};

const previewUrlFor = (file) =>
  mediaTypeOf(file) === "image" && typeof URL.createObjectURL === "function"
    ? URL.createObjectURL(file)
    : null;

const releasePreviewUrl = (pendingFile) => {
  if (pendingFile?.previewUrl && typeof URL.revokeObjectURL === "function") {
    URL.revokeObjectURL(pendingFile.previewUrl);
  }
};

const firstFileOf = (dataTransfer) => Array.from(dataTransfer?.files || [])[0] || null;

export const AUDIO_WITHOUT_CAPTION_NOTICE =
  "Los audios de WhatsApp van sin epígrafe.";

const WhatsAppComposer = ({
  accountId = null,
  thread,
  onSend,
  onSendMedia,
  sending = false,
  error = null,
  onClearError,
  disabled = false,
  extraActions = null,
}) => {
  const [body, setBody] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [fileError, setFileError] = useState(null);
  const threadIdRef = useRef(thread?.id);

  useEffect(() => {
    threadIdRef.current = thread?.id;
    setBody("");
    setPendingFile(null);
    setFileError(null);
  }, [thread?.id]);

  useEffect(() => () => releasePreviewUrl(pendingFile), [pendingFile]);

  const windowOpen = Boolean(thread?.window_open);

  useEffect(() => {
    if (windowOpen) return;
    setPendingFile(null);
    setPickerOpen(false);
  }, [windowOpen]);
  const hasInbound = Boolean(thread?.last_inbound_at);
  const canType = windowOpen && !disabled;
  const closedNotice = hasInbound ? WINDOW_CLOSED_NOTICE : NO_INBOUND_NOTICE;
  const phoneHint = thread?.whatsapp_account?.on_business_app ? PHONE_APP_HINT : null;
  const pendingIsAudio = pendingFile ? mediaTypeOf(pendingFile) === "audio" : false;
  const canAttach = canType && !sending && !uploading;
  const canSend = canAttach && (Boolean(pendingFile) || Boolean(body.trim()));

  const openPicker = () => {
    if (!canAttach) return;
    if (!accountId) {
      setFileError(NO_ACCOUNT_MESSAGE);
      return;
    }
    setFileError(null);
    setPickerOpen(true);
  };

  const takeAttachment = (attachment) => {
    const candidate = {
      id: attachment?.id,
      name: attachmentFileName(attachment),
      size: getAttachmentSizeInBytes(attachment) ?? 0,
    };
    const problem = validateWhatsAppFile(candidate);
    if (problem) {
      setFileError(problem);
      return false;
    }
    setFileError(null);
    setPendingFile(candidate);
    if (mediaTypeOf(candidate) === "audio") setBody("");
    return true;
  };

  const takeDirectFile = (file) => {
    const problem = validateWhatsAppFile(file);
    if (problem) {
      setFileError(problem);
      return false;
    }
    setFileError(null);
    setPendingFile({ name: file.name, size: file.size, file, previewUrl: previewUrlFor(file) });
    if (mediaTypeOf(file) === "audio") setBody("");
    return true;
  };

  const handlePickExisting = (attachment) => {
    setPickerOpen(false);
    takeAttachment(attachment);
  };

  const handleUploadWithoutSaving = (file) => {
    setPickerOpen(false);
    if (file) takeDirectFile(file);
  };

  const handleUploadNew = async (file) => {
    setPickerOpen(false);
    if (!file) return;
    const problem = validateWhatsAppFile(file);
    if (problem) {
      setFileError(problem);
      return;
    }
    if (SENT_WITHOUT_SAVING_TYPES.has(mediaTypeOf(file))) {
      takeDirectFile(file);
      return;
    }

    const form = new FormData();
    form.append("file", file);
    form.append("account_id", String(accountId));

    const uploadThreadId = threadIdRef.current;
    setUploading(true);
    setFileError(null);
    try {
      const created = await templatesService.createTemplateFileAttachment(form);
      if (threadIdRef.current !== uploadThreadId) return;
      if (!created?.id) {
        setFileError(UPLOAD_ERROR_MESSAGE);
        return;
      }
      takeAttachment({ ...created, size: getAttachmentSizeInBytes(created) ?? file.size });
    } catch (error) {
      if (threadIdRef.current !== uploadThreadId) return;
      if (error?.response?.status === 400) {
        takeDirectFile(file);
        return;
      }
      setFileError(extractApiErrorMessage(error) || UPLOAD_ERROR_MESSAGE);
    } finally {
      setUploading(false);
    }
  };

  const handlePaste = (event) => {
    const file = firstFileOf(event.clipboardData);
    if (!file || !canAttach) return;
    event.preventDefault();
    takeDirectFile(file);
  };

  const handleDragOver = (event) => {
    if (!canAttach) return;
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setDragging(false);
  };

  const handleDrop = (event) => {
    setDragging(false);
    if (!canAttach) return;
    event.preventDefault();
    const file = firstFileOf(event.dataTransfer);
    if (file) takeDirectFile(file);
  };

  const submit = async () => {
    if (!canSend) return;
    const text = body.trim();
    if (pendingFile) {
      const sent = await onSendMedia?.(
        pendingFile.file
          ? { file: pendingFile.file, caption: text }
          : { attachmentId: pendingFile.id, caption: text },
      );
      if (sent) {
        setBody("");
        setPendingFile(null);
      }
      return;
    }
    const sent = await onSend?.(text);
    if (sent) setBody("");
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <div
      className={`${styles.composer} ${dragging ? styles.composerDragging : ""}`}
      data-testid="whatsapp-composer"
      onDragOver={handleDragOver}
      onDragEnter={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {error && (
        <p className={styles.composerError} role="alert">
          {error}
          {onClearError && (
            <button
              type="button"
              className={styles.composerErrorDismiss}
              onClick={onClearError}
              aria-label="Cerrar aviso"
            >
              ×
            </button>
          )}
        </p>
      )}
      {fileError && (
        <p className={styles.composerError} role="alert">
          {fileError}
          <button
            type="button"
            className={styles.composerErrorDismiss}
            onClick={() => setFileError(null)}
            aria-label="Cerrar aviso del archivo"
          >
            ×
          </button>
        </p>
      )}
      {!windowOpen && (
        <div className={styles.composerClosed}>
          <p className={styles.composerInfo} role="status">
            {closedNotice}
          </p>
          {phoneHint && <p className={styles.composerInfo}>{phoneHint}</p>}
          {extraActions && <div className={styles.composerActions}>{extraActions}</div>}
        </div>
      )}
      {windowOpen && dragging && (
        <p className={styles.composerInfo} role="status">
          {DROP_HINT}
        </p>
      )}
      {windowOpen && pendingFile && (
        <div className={styles.pendingFile}>
          <span className={styles.pendingFileInfo}>
            {pendingFile.previewUrl && (
              <img className={styles.pendingFilePreview} src={pendingFile.previewUrl} alt="" />
            )}
            <span className={styles.pendingFileName}>
              {pendingFile.name} · {formatFileSize(pendingFile.size)}
            </span>
            {pendingFile.file && <span className={styles.pendingFileNote}>{WITHOUT_SAVING_NOTE}</span>}
          </span>
          <button
            type="button"
            className={styles.pendingFileRemove}
            onClick={() => setPendingFile(null)}
            aria-label="Quitar archivo"
            disabled={sending}
          >
            ×
          </button>
        </div>
      )}
      {windowOpen && (
        <div className={styles.composerRow}>
          <textarea
            className={styles.composerInput}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={
              pendingIsAudio
                ? AUDIO_WITHOUT_CAPTION_NOTICE
                : pendingFile
                  ? "Epígrafe (opcional)"
                  : "Escribí un mensaje..."
            }
            rows={2}
            disabled={!canType || sending || pendingIsAudio}
            aria-label="Mensaje de WhatsApp"
          />
          <div className={styles.composerActions}>
            <Button
              variant="secondary"
              tone="neutral"
              onClick={openPicker}
              loading={uploading}
              disabled={!canType || sending}
            >
              Adjuntar
            </Button>
            {extraActions}
            <Button
              variant="primary"
              tone="green"
              loading={sending}
              disabled={!canSend}
              onClick={submit}
            >
              Enviar
            </Button>
          </div>
        </div>
      )}

      {pickerOpen && (
        <AttachmentPickerModal
          accept={ACCEPT_ATTRIBUTE}
          accountId={accountId}
          linkedAttachmentIds={pendingFile?.id ? [pendingFile.id] : []}
          onClose={() => setPickerOpen(false)}
          onSelectExisting={handlePickExisting}
          onUploadNew={handleUploadNew}
          onUploadWithoutSaving={handleUploadWithoutSaving}
          onError={setFileError}
        />
      )}
    </div>
  );
};

export default WhatsAppComposer;
